/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-empty-function */
import { ChevronDown, Search, X } from "lucide-react";
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import lodash from "lodash";
import {
  Radio,
  MultiSelect,
  Checkbox,
  RadioGroup,
  Loader,
} from "@mantine/core";
import { Navigate } from "react-router-dom";
import {
  IBlockedDateEmployees,
  IBlockedDateEvent,
  IGroupedBlockedDateEvents,
} from "../../@types/blockedDates";
import { queryClient } from "../../../../App";
import { AuthContext } from "../../../../providers/AuthProvider";
import useEmployeeListApiService from "../../../employees/services/api/EmployeeListApiService";

interface IBlockDatesFilterProps {
  groupedBlockedDates: IGroupedBlockedDateEvents;
  odpBlockedDates: IBlockedDateEvent[];
  setGroupBlocks: React.Dispatch<
    React.SetStateAction<IGroupedBlockedDateEvents>
  >;
  searchedEmployees: ItemProps[];
  setSearchedEmployees: React.Dispatch<React.SetStateAction<ItemProps[]>>;
}

export interface ItemProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "id">,
    IBlockedDateEmployees {
  id: number; // Remain as number
  value: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ selected, ...others }, ref) => (
    <div
      {...others}
      ref={ref}
      id={others.id.toString()}
      className="flex items-center gap-3 px-2 py-2 rounded cursor-pointer hover:bg-gray-100"
    >
      <Checkbox checked={selected} size="xs" onChange={() => {}} />
      {others.employee_number || others.employee_id_number} -{" "}
      {others.employee_name}
    </div>
  )
);
SelectItem.displayName = "SelectItem";

const ValueItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ value, onSelect, ...others }, ref) => (
    <div
      id={others.id.toString()}
      ref={ref}
      className="flex items-center gap-3 px-2 py-1 mx-1 text-xs text-white rounded-full cursor-pointer bg-primary-base"
    >
      <span>{value}</span>
      <X onClick={onSelect} size={14} />
    </div>
  )
);
ValueItem.displayName = "ValueItem";

const BlockDatesFilter: React.FC<IBlockDatesFilterProps> = ({
  groupedBlockedDates,
  odpBlockedDates,
  setGroupBlocks,
  searchedEmployees,
  setSearchedEmployees,
}) => {
  const auth = useContext(AuthContext);

  const [search, setSearch] = useState<string>("");
  const [filterBy, setFilterBy] = useState<"all" | "company" | "individual">(
    "all"
  );

  const { isFetching, data: employeeData } = useEmployeeListApiService({
    employer: auth?.employer.id ?? 0,
    pagination: false,
    search,
    employee_limit: 10,
  });

  const handleSelect = (id: number): void => {
    setSearchedEmployees((prev) =>
      prev.map((employee) => {
        if (employee.employee_id === id) {
          return { ...employee, selected: !employee.selected };
        }
        return employee;
      })
    );
  };

  useEffect(() => {
    const employeeIdSet = new Set<string | undefined>();

    const tempEmployees: ItemProps[] = odpBlockedDates.flatMap(
      (blockDate) =>
        blockDate.employees
          ?.filter((employee) => {
            if (employeeIdSet.has(employee.employee_id_number)) {
              return false;
            }
            employeeIdSet.add(employee.employee_id_number);
            return true;
          })
          .map((employee) => ({
            ...employee,
            selected: false,
            id: employee.employee_id,
            value:
              employee.employee_number ||
              employee.employee_id_number ||
              employee.employee_id.toString(),
            label:
              employee.employee_number ||
              employee.employee_id_number ||
              employee.employee_name,
            onSelect: () => handleSelect(employee.employee_id),
          })) || []
    );

    setSearchedEmployees(tempEmployees);
  }, [odpBlockedDates]);

  useEffect(() => {
    if (employeeData) {
      const existingSelected = new Map(
        searchedEmployees.map((emp) => [emp.employee_id, emp.selected])
      );

      const newEmployees = employeeData.results
        .filter((employee) => employee.employee_no)
        .map((employee) => ({
          selected: existingSelected.get(employee.id) || false,
          id: employee.id,
          value: employee.employee_no,
          label: employee.employee_no,
          employee_id: employee.id,
          employee_number: employee.employee_no,
          employee_name: `${employee.first_name} ${employee.last_name}`,
          mobile_number: employee.mobile_number,
          onSelect: () => handleSelect(employee.id),
        }));

      const employeeIdSet = new Set(
        searchedEmployees.map((emp) => emp.employee_id)
      );
      const combinedEmployees = [
        ...searchedEmployees.map((emp) => ({
          ...emp,
          selected: existingSelected.get(emp.employee_id) || false,
          onSelect: () => handleSelect(emp.employee_id),
        })),
        ...newEmployees.filter((emp) => !employeeIdSet.has(emp.employee_id)),
      ];

      setSearchedEmployees(combinedEmployees);
    }
  }, [employeeData]);

  useEffect(() => {
    const selectedEmployees = searchedEmployees.filter(
      (employee) => employee.selected
    );

    if (filterBy === "all") {
      if (selectedEmployees.length === 0) {
        setGroupBlocks(groupedBlockedDates);
        return;
      }

      const filterEventsBySelectedEmployees = (
        events: IBlockedDateEvent[]
      ): IBlockedDateEvent[] =>
        events.filter(
          (event) =>
            !event.employees ||
            event.employees.some((emp) =>
              selectedEmployees.some(
                (employee) => emp.employee_id === employee.employee_id
              )
            )
        );

      setGroupBlocks({
        pastEvents: filterEventsBySelectedEmployees(
          groupedBlockedDates.pastEvents
        ),
        futureEvents: filterEventsBySelectedEmployees(
          groupedBlockedDates.futureEvents
        ),
        currentEvents: filterEventsBySelectedEmployees(
          groupedBlockedDates.currentEvents
        ),
      });
      return;
    }
    if (filterBy === "company") {
      const filterEventsForCompany = (
        events: IBlockedDateEvent[]
      ): IBlockedDateEvent[] => events.filter((event) => !event.employees);

      setGroupBlocks({
        pastEvents: filterEventsForCompany(groupedBlockedDates.pastEvents),
        futureEvents: filterEventsForCompany(groupedBlockedDates.futureEvents),
        currentEvents: filterEventsForCompany(
          groupedBlockedDates.currentEvents
        ),
      });
      return;
    }

    const filterEventsForIndividuals = (
      events: IBlockedDateEvent[]
    ): IBlockedDateEvent[] => {
      if (selectedEmployees.length === 0) {
        return events.filter((event) => event.employees);
      }
      return events.filter((event) =>
        event.employees?.some((emp) =>
          selectedEmployees.some(
            (employee) => emp.employee_id === employee.employee_id
          )
        )
      );
    };

    setGroupBlocks({
      pastEvents: filterEventsForIndividuals(groupedBlockedDates.pastEvents),
      futureEvents: filterEventsForIndividuals(
        groupedBlockedDates.futureEvents
      ),
      currentEvents: filterEventsForIndividuals(
        groupedBlockedDates.currentEvents
      ),
    });
  }, [searchedEmployees, groupedBlockedDates, filterBy]);

  const debouncedSearchTermInput = useCallback(
    lodash.debounce((searchInputValue) => {
      if (searchInputValue.length === 0) {
        return;
      }
      queryClient.invalidateQueries([
        "EmployeeListApiService",
        "employer",
        "pagination",
        "tsAndCsAccepted",
        "search",
        "job_id",
      ]);

      setSearch(searchInputValue);
    }, 500),
    []
  );

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <div className="pt-8 pb-5">
      <div className="flex flex-row items-end justify-between gap-10">
        <RadioGroup
          label="Filter by"
          spacing="xs"
          size="xs"
          value={filterBy}
          onChange={(value) =>
            setFilterBy(value as "all" | "company" | "individual")
          }
          classNames={{
            label: "contents",
            radioWrapper: "gap-2",
          }}
        >
          <Radio label="All Blocks" value="all" />
          <Radio label="Company Blocks" value="company" />
          <Radio label="Individual Blocks" value="individual" />
        </RadioGroup>
        <MultiSelect
          className="max-w-xl grow"
          data={searchedEmployees}
          placeholder="Filter by employee name or number"
          nothingFound={
            isFetching ? "Loading..." : "Employee not found in blocked dates."
          }
          searchable
          filter={(value: string, _: boolean, item: ItemProps) =>
            item.employee_name.toLowerCase().includes(value.toLowerCase()) ||
            item.employee_name.includes(value.toLowerCase()) ||
            item.value.includes(value) ||
            item.mobile_number?.includes(value)
          }
          value={searchedEmployees
            .filter((employee) => employee.selected)
            .map((employee) => employee.value)}
          itemComponent={SelectItem}
          valueComponent={ValueItem}
          rightSection={
            isFetching ? <Loader size="sm" /> : <ChevronDown size={14} />
          }
          icon={<Search size={14} />}
          onSearchChange={(value) => {
            setSearch(value);
            debouncedSearchTermInput(value);
          }}
          onChange={(value) => {
            setSearchedEmployees((prev) => {
              const newSelected = new Set(value);
              return prev.map((employee) => ({
                ...employee,
                selected: newSelected.has(employee.value),
              }));
            });
          }}
        />
      </div>
    </div>
  );
};

export default BlockDatesFilter;
